import type {
  ConnectionDeleteResult,
  ConnectionListResult,
  ConnectionRepository,
  ConnectionVerifyResult,
  NormalizedConnection,
  PlatformUrn,
} from '@mint-lib/integration-registry';
import { useQuery } from '@mint-lib/query-client';
import type { QueryClient } from '@mint-lib/query-client';
import { Connection } from '@mint-sdk/api-omni';

/**
 * Ported from the "default" (non-adserver, non-GA4) branch of
 * `packages/connections/src/api/queries/useGetConnectionList.ts`,
 * `useVerifyConnection.ts` and
 * `packages/connections/src/features/ManageConnection/useDeleteConnection.tsx` —
 * the repository every generic omni platform (and the amazon/zemanta
 * packages) share.
 */

interface OmniApiError {
  response?: { data?: { error?: { type?: string; message?: string } } };
}

function toNormalizedOmniConnection(
  urn: PlatformUrn,
  item: {
    id: string;
    name: string | null;
    usingInAccounts?: boolean | null;
    userUpdatedId?: string | null;
    amazonRegion?: string | null;
  },
): NormalizedConnection {
  return {
    id: item.id,
    name: item.name ?? '',
    platformUrn: urn,
    isVerified: null,
    isExpired: false,
    usingInAccounts: Boolean(item.usingInAccounts),
    userId: item.userUpdatedId ?? null,
    extra: { amazonRegion: item.amazonRegion ?? null },
  };
}

function useList(params: { platform: string }): ConnectionListResult {
  const query = useQuery({
    ...Connection.getBffConnectionList({
      params: { platform: params.platform, cache: 'skip' },
    }),
  });

  const connections = (query.data ?? []).map((item) =>
    toNormalizedOmniConnection(params.platform as PlatformUrn, item),
  );

  return { connections, isLoading: query.isLoading, refetch: query.refetch };
}

function useVerify(params: {
  platform: string;
  connectionId: string;
}): ConnectionVerifyResult {
  const query = useQuery({
    ...Connection.verify({
      params: { platform: params.platform },
      urlParams: { connectionId: params.connectionId },
    }),
    enabled: Boolean(params.connectionId && params.platform),
  });

  const apiError = query.error as OmniApiError | null;

  return {
    isVerified: query.data,
    isLoading: query.isLoading,
    isWrongCustomerError:
      apiError?.response?.data?.error?.type === 'WrongCustomerError',
    refetch: query.refetch,
  };
}

function useDelete(): ConnectionDeleteResult {
  const { mutate, isPending } = Connection.deleteConnection();

  return {
    remove: (id, { onSuccess, onError }) => {
      mutate({ urlParams: { connectionId: id } }, { onSuccess, onError });
    },
    isPending,
  };
}

/** `ConnectionRepository` implementation shared by every generic omni definition. */
export const omniConnectionRepository: ConnectionRepository = {
  useList,
  useVerify,
  useDelete,
};

/**
 * Loader-safe (non-hook) equivalent of `useList`, for `makeOmniIntegration`'s
 * `prefetch`. Router loaders run outside React, so they can't call the
 * `useList` hook — they fetch the same query directly through the
 * `QueryClient` instead.
 */
export async function fetchOmniConnections(
  queryClient: QueryClient,
  urn: PlatformUrn,
): Promise<NormalizedConnection[]> {
  const items = await queryClient.ensureQueryData(
    Connection.getBffConnectionList({
      params: { platform: urn, cache: 'skip' },
    }),
  );
  return (items ?? []).map((item) => toNormalizedOmniConnection(urn, item));
}
